/**
 * Whether the кружочек is filmed by the phone rather than by the page.
 *
 * There is one bundle for both native platforms, so `isNative` is the wrong
 * question here — Android's WebView records кружочки perfectly well and keeps
 * doing so. What this answers is narrower: is there an iOS plugin on the other
 * side of the bridge that will film this take with AVFoundation instead of
 * MediaRecorder. See lib/nativeVideoNote.ts for why that matters, and
 * README.md's paragraph on the canvas for how long it took to get here.
 *
 * Asked of `window.Capacitor` directly rather than through `@capacitor/core`,
 * for two reasons. The runtime injects that object before the bundle is
 * evaluated, so the answer is available synchronously — and canFlipCamera has to
 * be synchronous, since it is read while a button is being drawn. And the web
 * bundle then never imports the bridge at all: on a page with no Capacitor this
 * module is one `undefined` check and a `false`.
 *
 * The same shape as api/origin.ts, and for the same reason: one module reads the
 * environment and everything else asks it, rather than every caller testing a
 * platform and drifting apart.
 */

interface CapacitorBridge {
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
}

/** The plugin's name on both sides — `jsName` in VideoNotePlugin.swift. */
export const VIDEO_NOTE_PLUGIN = 'VideoNote';

function bridge(): CapacitorBridge | undefined {
  return (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;
}

/**
 * True only inside the iOS app, and only when the plugin is actually registered
 * there. Both halves are load-bearing: a bundle running in the shell before the
 * plugin exists must take the web path rather than call into nothing, which is
 * what makes the TypeScript side landable ahead of the Swift.
 */
export const hasNativeVideoNote: boolean = (() => {
  const capacitor = bridge();
  if (capacitor?.getPlatform?.() !== 'ios') return false;
  return capacitor.isPluginAvailable?.(VIDEO_NOTE_PLUGIN) === true;
})();
